if (!customElements.get('photo-story')) {
  class PhotoStory extends HTMLElement {
    connectedCallback() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.cleanups = [];
      this.setupExplorers();
      this.setupLightbox();
      if (this.reducedMotion) return;
      this.classList.add('is-ready');
      this.setupReveal();
      this.setupScrollEffects();
    }

    disconnectedCallback() {
      this.cleanups.forEach((cleanup) => cleanup());
      this.cleanups = [];
    }

    setupReveal() {
      const items = [...this.querySelectorAll('[data-reveal]')];
      if (!('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
      items.forEach((item) => observer.observe(item));
      this.cleanups.push(() => observer.disconnect());
    }

    setupScrollEffects() {
      const progressBar = this.querySelector('[data-progress]');
      const parallax = [...this.querySelectorAll('[data-parallax]')];
      const zooms = [...this.querySelectorAll('[data-zoom]')];
      const rails = [...this.querySelectorAll('[data-rail]')];
      const wide = window.matchMedia('(min-width: 750px)');
      const clamp = (value) => Math.max(0, Math.min(1, value));

      const measureRails = () => {
        rails.forEach((rail) => {
          const track = rail.querySelector('[data-rail-track]');
          rail.classList.toggle('is-pinned', wide.matches);
          if (!wide.matches) {
            rail.style.removeProperty('--rail-height');
            track.style.removeProperty('--rail-x');
            return;
          }
          const distance = Math.max(0, track.scrollWidth - window.innerWidth);
          rail.railDistance = distance;
          rail.style.setProperty('--rail-height', `${window.innerHeight + distance}px`);
        });
      };

      let frame = 0;
      const update = () => {
        frame = 0;
        const viewport = window.innerHeight;
        const bounds = this.getBoundingClientRect();

        if (progressBar) {
          const read = clamp(-bounds.top / Math.max(1, bounds.height - viewport));
          progressBar.style.setProperty('--progress', read.toFixed(4));
        }

        parallax.forEach((item) => {
          const host = item.parentElement.getBoundingClientRect();
          if (host.bottom < -200 || host.top > viewport + 200) return;
          const speed = parseFloat(item.dataset.parallax) || 0.2;
          const offset = (host.top + host.height / 2 - viewport / 2) * -speed;
          item.style.setProperty('--py', `${offset.toFixed(1)}px`);
        });

        zooms.forEach((zoom) => {
          const rect = zoom.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > viewport) return;
          const progress = clamp(-rect.top / Math.max(1, rect.height - viewport));
          const eased = progress * progress * (3 - 2 * progress);
          const requested = parseFloat(getComputedStyle(zoom).getPropertyValue('--zoom-max')) || 3;
          // Narrow screens already crop the photo hard, so cap the zoom to keep it sharp.
          const max = wide.matches ? requested : Math.min(requested, 2);
          zoom.style.setProperty('--zoom-now', (1 + (max - 1) * eased).toFixed(3));
          zoom.style.setProperty('--zoom-text', (1 - clamp(progress * 2.5)).toFixed(3));
        });

        rails.forEach((rail) => {
          const track = rail.querySelector('[data-rail-track]');
          const items = track.children.length;
          let progress;
          if (rail.classList.contains('is-pinned')) {
            const rect = rail.getBoundingClientRect();
            progress = clamp(-rect.top / Math.max(1, rect.height - viewport));
            track.style.setProperty('--rail-x', `${(-progress * (rail.railDistance || 0)).toFixed(1)}px`);
          } else {
            progress = clamp(track.scrollLeft / Math.max(1, track.scrollWidth - track.clientWidth));
          }
          const counter = rail.querySelector('[data-rail-count]');
          if (counter) counter.textContent = String(Math.min(items, Math.round(progress * (items - 1)) + 1)).padStart(2, '0');
        });
      };

      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const resize = () => { measureRails(); schedule(); };

      measureRails();
      update();
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', resize);
      wide.addEventListener('change', resize);
      rails.forEach((rail) => rail.querySelector('[data-rail-track]').addEventListener('scroll', schedule, { passive: true }));
      // Images in the rail load lazily and change its width.
      this.querySelectorAll('.photo-story__rail-item img').forEach((img) => img.addEventListener('load', resize, { once: true }));

      this.cleanups.push(() => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', resize);
        wide.removeEventListener('change', resize);
        cancelAnimationFrame(frame);
      });
    }

    setupExplorers() {
      this.querySelectorAll('[data-explorer]').forEach((explorer) => {
        const spots = [...explorer.querySelectorAll('[data-spot]')];
        const panels = [...explorer.querySelectorAll('[data-spot-panel]')];
        const intro = explorer.querySelector('[data-spot-intro]');
        if (!spots.length) return;
        let current = -1;
        const select = (index, focusPanel = false) => {
          current = (index + spots.length) % spots.length;
          spots.forEach((spot, i) => spot.setAttribute('aria-expanded', String(i === current)));
          panels.forEach((panel, i) => { panel.hidden = i !== current; });
          if (intro) intro.hidden = true;
          if (focusPanel) spots[current].focus();
          if (window.matchMedia('(max-width: 989px)').matches && !focusPanel) {
            panels[current].scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
          }
        };
        spots.forEach((spot, i) => spot.addEventListener('click', () => select(i)));
        explorer.querySelectorAll('[data-spot-step]').forEach((button) => {
          button.addEventListener('click', () => select(current + Number(button.dataset.spotStep), true));
        });
      });
    }

    setupLightbox() {
      const dialog = this.querySelector('[data-lightbox-dialog]');
      const triggers = [...this.querySelectorAll('[data-lightbox]')];
      if (!dialog || !triggers.length || typeof dialog.showModal !== 'function') return;

      const image = dialog.querySelector('[data-lightbox-image]');
      const caption = dialog.querySelector('[data-lightbox-caption]');
      const count = dialog.querySelector('[data-lightbox-count]');
      let index = 0;
      let lastTrigger = null;

      const show = (next) => {
        index = (next + triggers.length) % triggers.length;
        const trigger = triggers[index];
        const thumb = trigger.querySelector('img');
        image.classList.add('is-loading');
        image.onload = () => image.classList.remove('is-loading');
        image.src = trigger.dataset.full;
        image.alt = thumb ? thumb.alt : '';
        caption.textContent = trigger.dataset.caption || (thumb ? thumb.alt : '');
        count.textContent = `${index + 1} / ${triggers.length}`;
        const preload = triggers[(index + 1) % triggers.length];
        if (preload) new Image().src = preload.dataset.full;
      };

      triggers.forEach((trigger, i) => trigger.addEventListener('click', () => {
        lastTrigger = trigger;
        show(i);
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }));

      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        lastTrigger?.focus({ preventScroll: true });
      });
      dialog.querySelector('[data-lightbox-close]').addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => show(index - 1));
      dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => show(index + 1));
      dialog.addEventListener('click', (event) => { if (event.target === dialog || event.target.tagName === 'FIGURE') dialog.close(); });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') show(index + 1);
        if (event.key === 'ArrowLeft') show(index - 1);
      });

      let startX = null;
      dialog.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
      dialog.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 50) show(index + (delta < 0 ? 1 : -1));
        startX = null;
      });
    }
  }

  customElements.define('photo-story', PhotoStory);
}
